/**
 * Popup landing page for the direct-OIDC login flow.
 *
 * Opened by LoginsPanel in a popup window. Validates the returned `state`
 * against the flow stashed in sessionStorage (mirroring
 * features/auth/context/AuthContext.handleOAuthCallback exactly), completes
 * the callback against the backend, notifies the opener via postMessage,
 * and closes itself.
 */

import { useEffect, useState, type ReactElement } from 'react';
import { loginsApi } from '../api/loginsApi';
import { readLoginFlow, clearLoginFlow, LOGIN_COMPLETE_MESSAGE } from '../loginFlowStorage';

export function LoginCallbackPage(): ReactElement {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const complete = async (): Promise<void> => {
      const params = new URLSearchParams(window.location.search);

      const idpError = params.get('error');
      if (idpError) {
        setError(params.get('error_description') ?? idpError);
        return;
      }

      const code = params.get('code');
      const returnedState = params.get('state');
      if (!code || !returnedState) {
        setError('Missing code or state in callback URL');
        return;
      }

      const flow = readLoginFlow();
      if (!flow) {
        setError('Login flow not found — please try again');
        return;
      }

      if (returnedState !== flow.state) {
        setError('State mismatch - possible CSRF attack');
        return;
      }

      try {
        await loginsApi.completeCallback(
          flow.provider,
          code,
          flow.code_verifier,
          flow.redirect_uri,
        );
        clearLoginFlow();
        // `Window.opener` is typed `any` in lib.dom — narrow before calling postMessage.
        const opener = window.opener as Window | null;
        opener?.postMessage({ type: LOGIN_COMPLETE_MESSAGE }, window.location.origin);
        window.close();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to complete login');
      }
    };

    void complete();
  }, []);

  return (
    <div className="login-callback-page">
      {error ? (
        <div className="login-callback-page__error" role="alert">
          <p>{error}</p>
          <button
            type="button"
            className="dialog__btn dialog__btn--secondary"
            onClick={() => { window.close(); }}
          >
            Close this window
          </button>
        </div>
      ) : (
        <p className="login-callback-page__status" role="status">
          Completing login...
        </p>
      )}
    </div>
  );
}
